import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import api from "@/lib/api";
import { Card, Pill, Spinner } from "@/components/ui-kit";
import { Flame, ShieldCheck, Clock, Target, CheckCircle2, Trophy, Star, Sparkles, Medal, Award, Lock } from "lucide-react";

const BADGE_ICON = { flame: Flame, trophy: Trophy, star: Star, sparkles: Sparkles, medal: Medal };

export default function Progress() {
  const { studentId } = useOutletContext();
  const [p, setP] = useState(null);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    api.get(`/students/${studentId}/progress`).then(({ data }) => setP(data));
    api.get(`/students/${studentId}/badges/catalog`).then(({ data }) => setCatalog(data));
  }, [studentId]);
  if (!p) return <Spinner label="Loading progress" />;

  const pct = p.total_skills ? Math.round((100 * p.mastered_count) / p.total_skills) : 0;
  const earnedCount = catalog.filter((b) => b.earned).length;

  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-1">My progress</h1>
      <p className="text-[var(--ns-muted)] mb-5">Keep the loop going — small daily wins compound.</p>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <Stat icon={Flame} tone="#D96B43" label="Day streak" value={p.student.streak} />
        <Stat icon={ShieldCheck} tone="#2A8555" label="Mastered" value={`${p.mastered_count}/${p.total_skills}`} sub={`${pct}%`} />
        <Stat icon={Clock} tone="#1E6B52" label="Time on task" value={`${p.time_on_task_min}m`} />
        <Stat icon={Target} tone="#C88A2B" label="Open gaps" value={p.gap_count} />
      </div>

      <Card className="p-5 mb-5" data-testid="achievements-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)]">
            <Award size={15} /> Trophy shelf
          </div>
          <span className="text-xs font-semibold text-[var(--ns-muted)]">{earnedCount} of {catalog.length} earned</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {catalog.map((b) => {
            const Icon = BADGE_ICON[b.icon] || Star;
            return (
              <div key={b.key} data-testid={`trophy-${b.key}`} title={b.description}
                className="rounded-[var(--ns-radius)] p-3 text-center flex flex-col items-center gap-1.5 transition-transform hover:-translate-y-0.5"
                style={{ background: b.earned ? "#FBF0DA" : "var(--ns-surface-subtle)", opacity: b.earned ? 1 : 0.7 }}>
                <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: b.earned ? "#F2D79A" : "var(--ns-border)", color: b.earned ? "#8A5E15" : "var(--ns-subtle)" }}>
                  {b.earned ? <Icon size={20} /> : <Lock size={16} />}
                </div>
                <div className="text-[11px] font-semibold leading-tight" style={{ color: b.earned ? "#1F2824" : "var(--ns-subtle)" }}>{b.title}</div>
              </div>
            );
          })}
        </div>
        {earnedCount === 0 && <p className="text-sm text-[var(--ns-muted)] mt-3">Master a skill or build a streak to light up your first trophy. No pressure — they'll come!</p>}
      </Card>

      <Card className="p-5 mb-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-3">Last 7 days (minutes)</div>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={p.trend}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--ns-subtle)" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => [`${v} min`, "Time"]} cursor={{ fill: "var(--ns-surface-subtle)" }} />
              <Bar dataKey="minutes" fill="var(--ns-primary)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-5 mb-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Closed this week</div>
        {p.closed_this_week.length ? (
          <div className="grid gap-2">{p.closed_this_week.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 size={16} className="text-[#2A8555]" /> {c.name}</div>
          ))}</div>
        ) : <p className="text-sm text-[var(--ns-muted)]">Nothing closed yet this week — your next mastery check counts!</p>}
      </Card>

      <Card className="p-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">Focus areas</div>
        {p.gaps.length ? <div className="flex flex-wrap gap-2">{p.gaps.map((g) => <Pill key={g.skill_id} tone="gap">{g.name}</Pill>)}</div>
          : <p className="text-sm text-[var(--ns-muted)]">No open gaps right now. 🎉</p>}
        {p.next_skills.length > 0 && (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mt-4 mb-2">Up next</div>
            <ol className="text-sm list-decimal ml-4 space-y-1">{p.next_skills.map((n, i) => <li key={i}>{n}</li>)}</ol>
          </>
        )}
      </Card>
    </div>
  );
}

function Stat({ icon: Icon, tone, label, value, sub }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-1.5" style={{ color: tone }}><Icon size={16} /><span className="text-xs font-semibold uppercase tracking-wide text-[var(--ns-subtle)]">{label}</span></div>
      <div className="mt-1 font-head text-2xl font-bold">{value} {sub && <span className="text-sm text-[var(--ns-muted)]">{sub}</span>}</div>
    </Card>
  );
}
